import { api, TOKEN_KEY } from "@/lib/api";

/**
 * BIZ-PS-03: the mobile app's "Edit my website" row (`more.tsx`'s `WEB_ONLY_VIEWS` branch) opens
 * this console with a one-time `?handoff=` code — a fresh mint, from an already-signed-in app
 * session, of the SAME code `GoogleCallback.jsx` has always consumed via
 * `POST /auth/google/session` (`mintWebHandoff()` / `POST /auth/web-handoff` on the mobile side;
 * `service.mint_handoff`/`consume_handoff` on the backend, reused exactly, not a second auth
 * path). Before this, the owner landed on a fresh "Sign in to {city}" OTP form despite already
 * being signed in on the app.
 *
 * Called once from `AppContext`'s own boot effect, before `refreshMe()`, so an owner with no
 * stored web token yet is already authenticated by the time `Guard` decides whether to redirect
 * to `/login`. Extracted to its own module (rather than inlined in the boot effect) so it is
 * unit-testable without mounting the whole provider.
 */
/** The Google sign-in callback route (`/auth/google`, under any `/<city>` prefix). */
export const GOOGLE_CALLBACK_PATH = /\/auth\/google\/?$/;

export async function consumeWebHandoff() {
  // The one-time code in `?handoff=` is ONE code with TWO possible consumers. On the Google
  // callback route `GoogleCallback.jsx` owns it; consuming it here too spent it before the page
  // could, so the signed-in user was shown "Google sign in did not complete" and bounced to /login.
  // This mobile deep-link path only ever lands on owner routes, so it never runs on the callback.
  if (GOOGLE_CALLBACK_PATH.test(window.location.pathname)) return false;

  // A tab that is already signed in keeps its own session rather than switching identity out
  // from under whatever the owner was doing — only acts when there is no token yet.
  if (localStorage.getItem(TOKEN_KEY)) return false;

  const params = new URLSearchParams(window.location.search);
  const handoff = params.get("handoff");
  if (!handoff) return false;

  try {
    const { data } = await api.post("/auth/google/session", { handoff }, { skipAuthPrompt: true });
    // Only the token is kept here — the boot effect's own `refreshMe()` right after this re-reads
    // it and sets `auth` from `/auth/me`, the same source of truth every other boot path uses.
    localStorage.setItem(TOKEN_KEY, data.access_token);
    return true;
  } catch (e) {
    // Already used, expired, or offline — fall through to the ordinary signed-out boot;
    // `refreshMe()` finds no token and `Guard` sends the owner to /login, exactly today's
    // behaviour for anyone who did not arrive with a handoff code at all.
    return false;
  } finally {
    // Single-use by design (`consume_handoff`) — strip it so a refresh of this same tab doesn't
    // retry an already-spent code.
    params.delete("handoff");
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (nextSearch ? `?${nextSearch}` : "") + window.location.hash,
    );
  }
}
